/**
 * Radial (DEC-116) — a rotary knob sending one absolute MIDI CC.
 *
 * Gesture is VERTICAL-DRAG, not circular-track. Every DAW does it this
 * way: circular tracking needs the pointer to stay on a thin annulus, is
 * miserable on a small target, and forces the atan2 math `RotationDial`
 * needed. Drag up raises, drag down lowers, and travel is normalised
 * against a fixed pixel throw so a small knob is not hypersensitive.
 *
 * Double-tap / double-click snaps back to `defaultValue`, reusing the
 * pointer-timing gesture DEC-110 established for the mixer fader's unity
 * reset (deliberately not native `dblclick`, which is unreliable on iPad).
 *
 * Show-mode guard (SD-5 / DEC-072) and the ~60Hz dispatch coalescing are
 * lifted from Fader.tsx unchanged — a knob is a continuous control and
 * should behave identically to the other continuous controls.
 */

import { createSignal, createEffect, onCleanup, For, type Accessor } from 'solid-js';
import { fireAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { ulid } from '../utils/ulid.ts';
import { editOwnsGesture, isEditMode, editItem, type RadialItem, type LayoutColor } from '../layout/store.ts';
import { GUARD_GRAB_HOLD_MS, GUARD_GRAB_MOVE_TOL } from '../performance/store.ts';
import type { MidiCcStep } from '@opbuttons/schema';
import { noteControlValue } from '../timeline/recorder.ts';
import { releaseTouch } from '../timeline/touch.ts';

interface RadialProps {
  item: RadialItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Coalesce dispatches to ~60Hz so a fast drag doesn't flood the wire. */
const DISPATCH_INTERVAL_MS = 16;
/** Pixels of vertical travel for the full 0..127 sweep. Fixed rather than
 *  proportional to the knob's height so a small knob isn't twitchy. */
const FULL_TRAVEL_PX = 180;
/** Two taps inside this window count as a double-tap (DEC-110's value). */
const DOUBLE_TAP_MS = 300;

export function Radial(props: RadialProps) {
  const [dragging, setDragging] = createSignal(false);
  // In-drag value lives HERE, not in the store. Writing to the store on
  // every move replaces the item object, which makes App.tsx's
  // `<For each={layoutItems()}>` recreate the row - destroying this
  // component and its pointer capture, so the drag dies after one event.
  // (Measured 2026-07-28: the row is removed+re-added once mid-drag and a
  // 90%->10% fader drag landed on its pointerdown value.) Dispatching is
  // continuous; the store is written once on release, which is also just
  // correct - a 60Hz drag has no business touching persistence, undo
  // history and the bridge-sync debounce on every frame.
  const [liveValue, setLiveValue] = createSignal<number | null>(null);
  const shownValue = () => liveValue() ?? props.item.value;
  let lastDispatchMs = 0;
  let pendingValue: number | null = null;
  let knobEl: HTMLDivElement | undefined;

  // Drag origin — value is computed as an OFFSET from where the press
  // started, so the knob never jumps to an absolute pointer position.
  let dragStartY = 0;
  let dragStartValue = 0;
  let lastTapMs = 0;

  const [grabbed, setGrabbed] = createSignal(false);
  let grabTimer: number | undefined;
  let grabStartX = 0;
  let grabStartY = 0;

  function beginPendingGrab(x: number, y: number): void {
    grabStartX = x;
    grabStartY = y;
    if (grabTimer !== undefined) clearTimeout(grabTimer);
    grabTimer = window.setTimeout(() => {
      grabTimer = undefined;
      setGrabbed(true);
      setDragging(true);
      dragStartY = grabStartY;
      dragStartValue = shownValue();
    }, GUARD_GRAB_HOLD_MS);
  }
  function cancelPendingGrab(): void {
    if (grabTimer !== undefined) { clearTimeout(grabTimer); grabTimer = undefined; }
  }
  onCleanup(() => { if (grabTimer !== undefined) clearTimeout(grabTimer); });
  createEffect(() => { if (isEditMode()) { cancelPendingGrab(); setGrabbed(false); } });

  function tokenFor(): string {
    return COLOR_TOKEN[props.item.color];
  }

  /* Face flags - see the note in Fader.tsx. Same hole, same fix: the radial
     is the fader's sibling (one CC, a value readout, no name element), so it
     had the same two inert Inspector toggles and would have been the next
     control the operator tried them on. */
  const name = () => props.item.name?.trim() ?? '';
  const showName = () => !props.item.hideName;
  const showInfo = () => !props.item.hideLabel;

  function sweep(): number {
    return props.item.sweepDeg ?? 270;
  }

  /** Detent count, or 0 for continuous. Clamped at 1 minimum. */
  function steps(): number {
    const n = props.item.steps;
    if (n === undefined || n === null || n <= 0) return 0;
    return Math.max(1, Math.floor(n));
  }

  /** Snap a raw 0..127 value onto the nearest detent. Continuous when no
   *  step count is set. A single step is a degenerate but legal config -
   *  the knob simply has one position. */
  function quantize(v: number): number {
    const n = steps();
    if (n === 0) return v;
    if (n === 1) return 0;
    const idx = Math.round((v / 127) * (n - 1));
    return Math.round((idx / (n - 1)) * 127);
  }

  function valueFromDrag(clientY: number): number {
    const dy = dragStartY - clientY;               // up = positive
    const delta = (dy / FULL_TRAVEL_PX) * 127;
    const raw = Math.max(0, Math.min(127, Math.round(dragStartValue + delta)));
    return quantize(raw);
  }

  /** Tick mark endpoints for each detent. Suppressed above 32 so a
   *  high step count does not turn the dial into a solid ring. */
  /** The tick ring.
   *
   *  This used to render NOTHING unless the radial was quantised (`steps >= 2`),
   *  which is why an ordinary continuous knob had a bare cap and a bare arc and
   *  did not read as a knob at all. The workbench draws a scale ring on every
   *  radial - a real knob has a scale printed around it whether or not it
   *  detents - so the ring is now unconditional.
   *
   *  The two jobs are separated by WEIGHT, not by presence, which is the
   *  workbench's own mechanism: minor ticks are the scale, major ticks are
   *  meaningful positions. Quantised radials promote their step positions to
   *  major, so the detents still read - and read better, because they now sit
   *  against a scale instead of floating.
   */
  function tickLines(): { x1: number; y1: number; x2: number; y2: number; major: boolean }[] {
    const SCALE_TICKS = 23;
    const n = steps();
    const quantised = n >= 2 && n <= 32;
    const out: { x1: number; y1: number; x2: number; y2: number; major: boolean }[] = [];

    for (let i = 0; i < SCALE_TICKS; i++) {
      const t = i / (SCALE_TICKS - 1);
      const a = (-sweep() / 2 + t * sweep() - 90) * (Math.PI / 180);
      // Unquantised: every 4th tick is major, as in the workbench. Quantised:
      // majors come from the step positions instead, added below.
      const major = !quantised && i % 4 === 0;
      const r1 = major ? 38.6 : 41.7;
      out.push({
        x1: 50 + r1 * Math.cos(a), y1: 50 + r1 * Math.sin(a),
        x2: 50 + 46.2 * Math.cos(a), y2: 50 + 46.2 * Math.sin(a),
        major,
      });
    }

    if (quantised) {
      for (let i = 0; i < n; i++) {
        const a = (-sweep() / 2 + (i / (n - 1)) * sweep() - 90) * (Math.PI / 180);
        out.push({
          x1: 50 + 38.6 * Math.cos(a), y1: 50 + 38.6 * Math.sin(a),
          x2: 50 + 46.2 * Math.cos(a), y2: 50 + 46.2 * Math.sin(a),
          major: true,
        });
      }
    }
    return out;
  }

  function dispatchValue(value: number): void {
    const now = performance.now();
    if (now - lastDispatchMs < DISPATCH_INTERVAL_MS) {
      pendingValue = value;
      return;
    }
    lastDispatchMs = now;
    pendingValue = null;
    // Fan out to every configured bridge (multi-select, 2026-08-13) —
    // undefined bridgeIds means the default bridge.
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      const step: MidiCcStep = {
        type: 'midi.cc',
        id: ulid(),
        target,
        channel: props.item.channel + 1,
        cc: props.item.ccNumber,
        value,
      };
      void fireAction(step);
    }
  }

  /** Live update: dispatch + local signal only. NO store write (see the
   *  liveValue comment above). */
  function commit(value: number): void {
    setLiveValue(value);
    dispatchValue(value);
    noteControlValue(props.item.id, undefined, value);
  }

  /** End of gesture: persist whatever the drag landed on, then hand the
   *  display back to the store. */
  function persist(): void {
    const v = liveValue();
    if (v !== null) editItem(props.item.id, { value: v } as Partial<RadialItem>);
    // The hand let go: a timeline holding this CC back takes it again per its touchMode (OPBridge S324).
    if (v !== null) releaseTouch(props.item.bridgeIds, [{ channel: props.item.channel, cc: props.item.ccNumber }]);
    setLiveValue(null);
  }

  function onPointerDown(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();

    const guarded = !isEditMode() && !!props.item.guard;
    if (guarded && !grabbed()) {
      e.currentTarget.setPointerCapture(e.pointerId);
      beginPendingGrab(e.clientX, e.clientY);
      return;
    }

    // Double-tap -> snap to default. Checked before starting a drag so the
    // second tap of the pair doesn't also nudge the value.
    const now = performance.now();
    if (now - lastTapMs < DOUBLE_TAP_MS) {
      lastTapMs = 0;
      lastDispatchMs = 0;
      commit(quantize(props.item.defaultValue ?? 0));
      persist();
      return;
    }
    lastTapMs = now;

    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    dragStartY = e.clientY;
    dragStartValue = shownValue();
  }

  function onPointerMove(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      if (Math.abs(e.clientX - grabStartX) > GUARD_GRAB_MOVE_TOL ||
          Math.abs(e.clientY - grabStartY) > GUARD_GRAB_MOVE_TOL) {
        cancelPendingGrab();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    commit(valueFromDrag(e.clientY));
  }

  function onPointerUp(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      cancelPendingGrab();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    setGrabbed(false);
    if (pendingValue !== null) {
      lastDispatchMs = 0;
      commit(pendingValue);
    }
    persist();
  }

  /** Pointer angle in degrees, centred on the sweep. 0 = straight up. */
  function angleDeg(): number {
    const frac = shownValue() / 127;
    return -sweep() / 2 + frac * sweep();
  }

  /** SVG arc path for the filled portion of the track. */
  function arcPath(fromFrac: number, toFrac: number): string {
    const r = 34;
    const a0 = (-sweep() / 2 + fromFrac * sweep() - 90) * (Math.PI / 180);
    const a1 = (-sweep() / 2 + toFrac * sweep() - 90) * (Math.PI / 180);
    const x0 = 50 + r * Math.cos(a0), y0 = 50 + r * Math.sin(a0);
    const x1 = 50 + r * Math.cos(a1), y1 = 50 + r * Math.sin(a1);
    const large = (toFrac - fromFrac) * sweep() > 180 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
  }

  return (
    <div
      class="radial"
      data-token={tokenFor()}
      data-dragging={dragging() ? 'true' : 'false'}
      data-grabbed={grabbed() ? 'true' : 'false'}
      ref={knobEl}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* The cap and pointer are anchored to THIS wrapper, not to `.radial`.
          `.radial` is a flex column that also holds the readout row, so
          centring against it put the cap ~9px below the SVG's own centre -
          the dial visibly sat low. The wrapper is the square the SVG actually
          draws in, which is the only box the 57.5% cap ratio is true against. */}
      <span class="radial-dial-wrap">
        <svg class="radial-dial" viewBox="0 0 100 100" aria-hidden="true">
          <path class="radial-track" d={arcPath(0, 1)} />
          <For each={tickLines()}>
            {(tk) => (
              <line
                class="radial-tick"
                data-major={tk.major ? 'true' : 'false'}
                x1={tk.x1} y1={tk.y1} x2={tk.x2} y2={tk.y2}
              />
            )}
          </For>
          <path class="radial-fill" d={arcPath(0, Math.max(0.0001, shownValue() / 127))} />
        </svg>
        {/* The cap left the SVG and became an HTML element (DEC-133 rule 3).
            As an <circle> it could only take `fill`, and --sk-rest-bg is a
            GRADIENT in five of the seven skins - an SVG paint cannot be one, so
            the cap could never carry the skin's material and rendered flat (or
            black) no matter what it was pointed at. Machined's bevel stack and
            Obsidian's chrome rim are box-shadows; they need a box. */}
        <span class="radial-body" aria-hidden="true" />
        <span
          class="radial-pointer"
          aria-hidden="true"
          style={{ transform: `translate(-50%, -100%) rotate(${angleDeg()}deg)` }}
        />
      </span>
      {showName() && name() && (
        <div class="radial-name">{name()}</div>
      )}
      {showInfo() && (
        <div class="radial-readout">
          <span class="radial-cc">cc {props.item.ccNumber} · ch {props.item.channel + 1}</span>
          <span class="radial-value">
            {shownValue()}{steps() >= 2 ? ` · ${Math.round((shownValue() / 127) * (steps() - 1)) + 1}/${steps()}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
